import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Save, X, Trash2 } from 'lucide-react';

const FIELD_CONFIG = {
  initials:  { label: 'Initials',   defaultW: 0.035, defaultH: 0.025 },
  signature: { label: 'Signature',  defaultW: 0.22, defaultH: 0.030 },
  date:      { label: 'Date',       defaultW: 0.14, defaultH: 0.030 },
  text:      { label: 'Text',       defaultW: 0.28, defaultH: 0.030 },
};

const TYPE_STYLES = {
  initials:  'border-blue-500 bg-blue-50 text-blue-800',
  signature: 'border-purple-500 bg-purple-50 text-purple-800',
  date:      'border-green-500 bg-green-50 text-green-800',
  text:      'border-amber-500 bg-amber-50 text-amber-800',
};

const TYPE_DOT = {
  initials:  'bg-blue-500',
  signature: 'bg-purple-500',
  date:      'bg-green-500',
  text:      'bg-amber-500',
};

// ── PdfPage ───────────────────────────────────────────────────────────────────

function PdfPage({
  pdf, pageNum, fields, pendingField, selectedId,
  onPageClick, onFieldMouseDown, onSelectField,
  onPendingLabelChange, onConfirmPending, onCancelPending,
  containerRef,
}) {
  const canvasRef = useRef();
  const labelInputRef = useRef();
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;
    const render = async () => {
      const page = await pdf.getPage(pageNum);
      const scale = Math.min(1.5, 780 / page.getViewport({ scale: 1 }).width);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      if (!cancelled) setRendered(true);
    };
    render();
    return () => { cancelled = true; };
  }, [pdf, pageNum]);

  useEffect(() => {
    if (pendingField && labelInputRef.current) labelInputRef.current.focus();
  }, [pendingField]);

  return (
    <div className="relative shadow-lg" style={{ display: 'inline-block' }}>
      <div className="absolute -top-6 left-0 text-xs text-muted-foreground font-medium">
        Page {pageNum}
      </div>
      <canvas ref={canvasRef} className="block" />
      {rendered && (
        <div
          ref={containerRef}
          className="absolute inset-0"
          style={{ cursor: 'crosshair' }}
          onClick={onPageClick}
        >
          {/* Placed fields */}
          {fields.map(field => (
            <div
              key={field.id}
              className={`absolute border-2 flex items-center px-1 select-none ${TYPE_STYLES[field.type]} ${
                selectedId === field.id ? 'ring-2 ring-primary ring-offset-1' : ''
              }`}
              style={{
                left: `${field.x * 100}%`,
                top: `${field.y * 100}%`,
                width: `${field.width * 100}%`,
                height: `${field.height * 100}%`,
                cursor: 'move',
                fontSize: 9,
              }}
              onClick={e => { e.stopPropagation(); onSelectField(field.id); }}
              onMouseDown={e => onFieldMouseDown(e, field)}
            >
              <span className="truncate">{field.label || FIELD_CONFIG[field.type].label}</span>
            </div>
          ))}

          {/* Pending field preview */}
          {pendingField && (
            <>
              <div
                className={`absolute border-2 border-dashed pointer-events-none ${TYPE_STYLES[pendingField.type]}`}
                style={{
                  left: `${pendingField.x * 100}%`,
                  top: `${pendingField.y * 100}%`,
                  width: `${pendingField.width * 100}%`,
                  height: `${pendingField.height * 100}%`,
                }}
              />
              {/* Floating label input */}
              <div
                className="absolute z-50 bg-white shadow-lg rounded border px-2 py-1 flex items-center gap-1.5"
                style={{
                  left: `${pendingField.x * 100}%`,
                  top: `${(pendingField.y + pendingField.height) * 100 + 0.5}%`,
                  minWidth: 200,
                }}
                onClick={e => e.stopPropagation()}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${TYPE_DOT[pendingField.type]}`} />
                <input
                  ref={labelInputRef}
                  className="text-xs border-none outline-none flex-1 min-w-0"
                  placeholder="Enter label (e.g. Parent Initials)…"
                  value={pendingField.label}
                  onChange={e => onPendingLabelChange(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') onConfirmPending();
                    if (e.key === 'Escape') onCancelPending();
                  }}
                />
                <button
                  className="text-green-600 hover:text-green-700 font-bold text-sm"
                  onClick={onConfirmPending}
                >✓</button>
                <button
                  className="text-red-500 hover:text-red-600 font-bold text-sm"
                  onClick={onCancelPending}
                >✕</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main editor ───────────────────────────────────────────────────────────────

export default function DocumentFieldEditor({ doc, onClose, onSaved }) {
  const { api } = useAuth();
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState(doc.fields || []);
  const [activeType, setActiveType] = useState('initials');
  const [pendingField, setPendingField] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const dragRef = useRef(null);
  const containerRefs = useRef({});

  // Load PDF via unauthenticated serve_file endpoint
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file/`;
        const pdf = await pdfjsLib.getDocument(url).promise;
        if (!cancelled) { setPdfDoc(pdf); setPageCount(pdf.numPages); }
      } catch {
        if (!cancelled) toast.error('Failed to load PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [doc.id]);

  // Global drag tracking
  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { fieldId, startX, startY, origX, origY, cw, ch, fw, fh } = dragRef.current;
    const dx = (e.clientX - startX) / cw;
    const dy = (e.clientY - startY) / ch;
    setFields(prev => prev.map(f =>
      f.id === fieldId
        ? { ...f, x: Math.max(0, Math.min(origX + dx, 1 - fw)), y: Math.max(0, Math.min(origY + dy, 1 - fh)) }
        : f
    ));
  }, []);

  const handleMouseUp = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handlePageClick = (e, pageNum) => {
    if (pendingField) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const cfg = FIELD_CONFIG[activeType];
    setPendingField({
      id: `field_${Date.now()}`,
      type: activeType,
      label: '',
      page: pageNum,
      x: Math.max(0, Math.min(x - cfg.defaultW / 2, 1 - cfg.defaultW)),
      y: Math.max(0, Math.min(y - cfg.defaultH / 2, 1 - cfg.defaultH)),
      width: cfg.defaultW,
      height: cfg.defaultH,
    });
  };

  const confirmPending = () => {
    if (!pendingField?.label?.trim()) { toast.error('Please enter a label'); return; }
    setFields(prev => [...prev, pendingField]);
    setSelectedId(pendingField.id);
    setPendingField(null);
  };

  const cancelPending = () => setPendingField(null);

  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(field.id);
    const container = containerRefs.current[field.page];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dragRef.current = {
      fieldId: field.id,
      startX: e.clientX, startY: e.clientY,
      origX: field.x, origY: field.y,
      cw: rect.width, ch: rect.height,
      fw: field.width, fh: field.height,
    };
  };

  const deleteField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleSave = async () => {
    if (pendingField) { toast.error('Confirm or cancel the field being placed first'); return; }
    setSaving(true);
    try {
      await api.patch(`/documents/${doc.id}/fields/`, { fields });
      toast.success('Fields saved');
      onSaved(fields);
    } catch {
      toast.error('Failed to save fields');
    } finally {
      setSaving(false);
    }
  };

  const selectedField = fields.find(f => f.id === selectedId);

  return (
    <div className="flex h-full">
      {/* ── Sidebar ── */}
      <div className="w-60 shrink-0 border-r flex flex-col bg-background">
        {/* Type selector */}
        <div className="p-4 border-b space-y-2">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Field Type</p>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(FIELD_CONFIG).map(([type, cfg]) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`px-2 py-1.5 rounded text-xs font-medium border-2 transition-colors ${
                  activeType === type
                    ? TYPE_STYLES[type]
                    : 'border-transparent text-muted-foreground hover:bg-muted'
                }`}
              >
                {cfg.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Click on the PDF to place a <strong>{FIELD_CONFIG[activeType].label}</strong> field.
            Drag to reposition.
          </p>
        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {fields.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">No fields yet.</p>
          )}
          {fields.map((f, i) => (
            <div
              key={f.id}
              onClick={() => setSelectedId(f.id)}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs ${
                selectedId === f.id ? 'bg-muted' : 'hover:bg-muted/50'
              }`}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${TYPE_DOT[f.type]}`} />
              <span className="flex-1 truncate">{f.label || `${FIELD_CONFIG[f.type].label} ${i + 1}`}</span>
              <span className="text-muted-foreground shrink-0">p{f.page}</span>
              <button
                onClick={e => { e.stopPropagation(); deleteField(f.id); }}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>

        {/* Selected field editor */}
        {selectedField && (
          <div className="p-3 border-t space-y-2">
            <p className="text-[11px] font-semibold uppercase text-muted-foreground">Edit Field</p>
            <Input
              className="h-7 text-xs"
              value={selectedField.label}
              onChange={e => setFields(prev => prev.map(f => f.id === selectedId ? { ...f, label: e.target.value } : f))}
              placeholder="Label…"
            />
            <Button
              size="sm"
              variant="destructive"
              className="w-full h-7 text-xs"
              onClick={() => deleteField(selectedField.id)}
            >
              <Trash2 className="h-3 w-3 mr-1" /> Delete
            </Button>
          </div>
        )}

        {/* Save */}
        <div className="p-3 border-t">
          <Button className="w-full" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Fields
          </Button>
        </div>
      </div>

      {/* ── PDF area ── */}
      <div className="flex-1 overflow-auto bg-muted/40 p-8">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-10">
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => (
              <PdfPage
                key={pageNum}
                pdf={pdfDoc}
                pageNum={pageNum}
                fields={fields.filter(f => f.page === pageNum)}
                pendingField={pendingField?.page === pageNum ? pendingField : null}
                selectedId={selectedId}
                onPageClick={e => handlePageClick(e, pageNum)}
                onFieldMouseDown={handleFieldMouseDown}
                onSelectField={setSelectedId}
                onPendingLabelChange={label => setPendingField(p => p ? { ...p, label } : p)}
                onConfirmPending={confirmPending}
                onCancelPending={cancelPending}
                containerRef={el => { containerRefs.current[pageNum] = el; }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
